import * as React from "react";
import Avatar from "@mui/joy/Avatar";
import AvatarGroup from "@mui/joy/AvatarGroup";
import { IdentityProps } from "../../utils/chat-types";

/**
 * Props interface for the GroupedAvatars component.
 * 
 * @interface GroupedAvatarsProps
 * @property {IdentityProps[]} members - Array of group members to display avatars for
 */
type GroupedAvatarsProps = {
  members: IdentityProps[];
};

/**
 * A component that displays a group of avatars with overflow handling.
 * Shows up to 2 avatars and a count for additional members.
 * 
 * @component
 * @param {GroupedAvatarsProps} props - The props for the GroupedAvatars component
 * @returns {JSX.Element} A group of avatars with overflow count
 * 
 * @example
 * ```tsx
 * <GroupedAvatars
 *   members={[
 *     { id: "1", username: "John", name: "John Doe" },
 *     { id: "2", username: "Jane", name: "Jane Doe" },
 *     { id: "3", username: "Bob", name: "Bob Smith" }
 *   ]}
 * />
 * ```
 */
export default function GroupedAvatars({ members }: GroupedAvatarsProps) {
  const avatars = Array.from({ length: members.length }, (_, index) => (
    <Avatar alt={members[index].username} key={index} />
  ));

  return (
    <AvatarGroup>
      {avatars.slice(0, 2)}
      {members.length > 2 && <Avatar>+{members.length - 2}</Avatar>}
    </AvatarGroup>
  );
}
